import React, { useMemo, useState } from 'react';
import { useData } from '../../store/DataContext';
import { Button } from '../../components/ui/Button';
import { Trash2Icon } from 'lucide-react';
import { expiryAlertWindow, isExpired } from './pharmacyUtils';
export function ExpiredMedicines() {
  const {
    currentUser,
    medicineBatches,
    medicines,
    recordStockMovement
  } = useData();
  const [message, setMessage] = useState('');
  const manager = currentUser?.role === 'Pharmacy Manager';
  const groups = useMemo(() => ({
    expired: medicineBatches.filter(batch => expiryAlertWindow(batch) === 'Expired'),
    days30: medicineBatches.filter(batch => expiryAlertWindow(batch) === '30 Days'),
    days60: medicineBatches.filter(batch => expiryAlertWindow(batch) === '60 Days'),
    days90: medicineBatches.filter(batch => expiryAlertWindow(batch) === '90 Days')
  }), [medicineBatches]);
  const dispose = async (batchId: string) => {
    const batch = medicineBatches.find(item => item.id === batchId);
    if (!batch || !confirm(`Dispose ${batch.quantityRemaining} blocked units from batch ${batch.batchNumber}?`)) return;
    const result = await recordStockMovement({
      medicineId: batch.medicineId,
      batchId: batch.id,
      quantityDelta: -batch.quantityRemaining,
      type: 'Expired Disposal',
      referenceType: 'Expiry',
      referenceId: batch.id,
      reason: 'Expired',
      notes: `Expired batch ${batch.batchNumber} disposed from blocked inventory.`
    });
    setMessage(result.ok ? 'Expired stock disposed and permanently recorded in movement history.' : result.error || 'Unable to dispose stock.');
  };
  return <div className="space-y-6" data-id="element-4860" data-mp="1ry841l7taykl">
      <div data-id="element-4861" data-mp="1rx6i2lp9cro3">
        <h2 className="text-xl font-bold text-slate-950" data-id="element-4862" data-mp="1rxg829aayyts">Expiry Management</h2>
        <p className="mt-1 text-sm text-slate-500" data-id="element-4863" data-mp="1ww008rfhp6ul">
          Expiry alerts update live at 90, 60, and 30 days. Expired batches are
          blocked from dispensing until disposal.
        </p>
      </div>
      {message && <p role="status" className="rounded-md bg-slate-100 px-3 py-2 text-sm text-slate-700" data-id="element-4864" data-mp="3o0utrophq8fb">
          {message}
        </p>}
      <BatchTable title="Expired — dispensing blocked" tone="red" batches={groups.expired} medicines={medicines} action={manager ? dispose : undefined} data-id="element-4865" data-mp="1tmosnihg3jt" />
      {!manager && groups.expired.length > 0 && <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-id="element-4866" data-mp="358s6vvm99vwm">
          Expired inventory remains blocked. A Pharmacy Manager must authorise
          disposal.
        </p>}
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3" data-id="element-4867" data-mp="2qlgyg2ldes0q">
        <BatchTable title="Within 30 days" tone="orange" batches={groups.days30} medicines={medicines} data-id="element-4868" data-mp="2qjvytyk78iw0" />
        <BatchTable title="Within 60 days" tone="amber" batches={groups.days60} medicines={medicines} data-id="element-4869" data-mp="2cfx1yki0raun" />
        <BatchTable title="Within 90 days" tone="amber" batches={groups.days90} medicines={medicines} data-id="element-4870" data-mp="2h90075xqr8h6" />
      </div>
    </div>;
}
function BatchTable({
  title,
  tone,
  batches,
  medicines,
  action
}: {
  title: string;
  tone: 'red' | 'orange' | 'amber';
  batches: ReturnType<typeof useData>['medicineBatches'];
  medicines: ReturnType<typeof useData>['medicines'];
  action?: (batchId: string) => void;
}) {
  const toneClass = tone === 'red' ? 'border-red-200 bg-red-50 text-red-800' : tone === 'orange' ? 'border-orange-200 bg-orange-50 text-orange-800' : 'border-amber-200 bg-amber-50 text-amber-800';
  return <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-4871" data-mp="19vg4j6ngqtzu">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4" data-id="element-4872" data-mp="1eqbum19537ab">
        <div data-id="element-4873" data-mp="1v0vy4pwaeqvi">
          <h3 className="font-bold text-slate-900" data-id="element-4874" data-mp="1v1e1s7la47dg">{title}</h3>
          <p className="mt-0.5 text-sm text-slate-500" data-id="element-4875" data-mp="1q82te20tjijf">
            {batches.length} active batch{batches.length === 1 ? '' : 'es'}{' '}
            require attention
          </p>
        </div>
        <span className={`rounded-full border px-2.5 py-1 text-xs font-bold ${toneClass}`} data-id="element-4876" data-mp="1gbzoioel15ss">
          {batches.reduce((sum, batch) => sum + batch.quantityRemaining, 0)}{' '}
          units
        </span>
      </div>
      <div className="overflow-x-auto" data-id="element-4877" data-mp="28x9ge18nawb1">
        <table className="min-w-full divide-y divide-slate-100" data-id="element-4878" data-mp="23zg8n3rp1u8n">
          <thead className="bg-slate-50" data-id="element-4879" data-mp="2422bwr7jxnl2">
            <tr data-id="element-4880" data-mp="1z04orlw168mu">
              {['Medicine', 'Batch', 'Expiry date', 'Blocked / available units', ...(action ? ['Action'] : [])].map(heading => <th key={heading} className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-4881" data-mp="noc8zns655d0">
                  {heading}
                </th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-4882" data-mp="3967ou2aawkz2">
            {batches.length ? batches.map(batch => {
            const medicine = medicines.find(item => item.id === batch.medicineId);
            const blocked = isExpired(batch);
            return <tr key={batch.id} data-id="element-4883" data-mp="3ry0hfvlgk469">
                    <td className="px-5 py-3.5 font-semibold text-slate-900" data-id="element-4884" data-mp="3rz1web5lhat9">
                      {medicine?.name || 'Unknown medicine'}
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-600" data-id="element-4885" data-mp="1xk9009ww0g09">
                      {batch.batchNumber}
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-600" data-id="element-4886" data-mp="22jvd8jcptw92">
                      {new Date(`${batch.expiryDate}T00:00:00`).toLocaleDateString()}
                    </td>
                    <td className={`px-5 py-3.5 text-sm font-semibold ${blocked ? 'text-red-700' : 'text-slate-900'}`} data-id="element-4887" data-mp="1qivqz87q6y3l">
                      {batch.quantityRemaining}{' '}
                      {blocked ? 'blocked' : 'available'}
                    </td>
                    {action && <td className="px-5 py-3.5 text-right" data-id="element-4888" data-mp="29fwzv44ehict">
                        <Button size="sm" variant="danger" onClick={() => action(batch.id)} data-id="element-4889" data-mp="29dkias2k1a0a">
                          <Trash2Icon className="mr-1.5 h-4 w-4" data-id="element-4890" data-mp="2ia1ulymnndxm" /> Dispose
                        </Button>
                      </td>}
                  </tr>;
          }) : <tr data-id="element-4891" data-mp="3bryawchgcfoo">
                <td colSpan={action ? 5 : 4} className="px-5 py-10 text-center text-sm text-slate-500" data-id="element-4892" data-mp="3brewv6uxabfx">
                  No batches in this alert window.
                </td>
              </tr>}
          </tbody>
        </table>
      </div>
    </section>;
}